<script lang="ts" setup>
import type { AliyahType, HaftarahType, PronunciationType, SentenceGroupType, SentenceType } from 'types/types'
import { computed, ref, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { useDisplay } from 'vuetify'

import AnnotatedText from '@/components/AnnotatedText.vue'
import FontSizeControls from '@/components/FontSizeControls.vue'
import PopOver from '@/components/PopOver.vue'
import RecordingSpinner from '@/components/RecordingSpinner.vue'
import RecordingUploadDialog from '@/components/RecordingUploadDialog.vue'
import SamplingOverlay from '@/components/SamplingOverlay.vue'
import TikkunToggle from '@/components/TikkunToggle.vue'
import TransformationSelect from '@/components/TransformationSelect.vue'
import { useChoosableTags } from '@/composables/choosableTags'
import { useFont } from '@/composables/font'
import { makeSentenceSegmentation } from '@/composables/segmentation'
import { useSentences } from '@/composables/sentences'
import { useStyledRecordings } from '@/composables/styledRecording'
import { useAnnotations } from '@/composables/useAnnotations'
import { useAudioPlayback } from '@/composables/useAudioPlayback'
import { useLooseRecording } from '@/composables/useLooseRecording'
import { usePopover } from '@/composables/usePopover'
import { usePopoverPlayback } from '@/composables/usePopoverPlayback'
import { loadPracticeSettings, savePracticeSettings } from '@/composables/usePracticeSettings'
import { useTransformation } from '@/composables/useTransformation'
import {
  LISTEN_TEXT_NAME,
  listenTextSegmentId,
  PITCH_SHIFT_STEP,
  UI_MAX_PITCH_SHIFT,
  UI_MIN_PITCH_SHIFT,
  uiPitchToActual,
} from '@/constants/practiceConstants'
import store from '@/store'

/** Slider width style for the labeled granularity slider (wider for text labels). */
const GRANULARITY_SLIDER_STYLE = 'min-width: 100px; max-width: 150px'

/** Slider width style for the pitch shift slider. */
const PITCH_SLIDER_STYLE = 'min-width: 80px; max-width: 120px'

/** Slider width style for the compact icon-based granularity slider. */
const COMPACT_GRANULARITY_SLIDER_STYLE = 'min-width: 60px; max-width: 100px'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { segmentPredicateAsWordPredicate } from '@/util/annotatedTextCallbacks'
import { AnnotatedWord } from '@/util/AnnotatedWord'
import { segmentationFromRecording } from '@/util/RecordingHelpers'
import { SegmentationType } from '@/util/Segmentation'
import { findTagByName, PHRASES, SENTENCE, WORDS } from '@/util/Tags'
import { absentStyle, SEGMENT_STYLES } from '@/util/TextStyles'
import { defaultVoiceStyleSet, hasMelody, inSingMode, READ, SING, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ props
interface Props {
  aliyah: AliyahType | null
  haftarah: HaftarahType | null
  sentenceGroup: SentenceGroupType | null
  sentences: SentenceType[]
}

const props = defineProps<Props>()
// }}}

const logger = useLogger()

// {{{ composables
const { font, fontSize: storedFontSize, lineHeight } = useFont()
const { pause, paused, pitchShift, playSegment, playing, resume } = useAudioPlayback(logger)
const { mdAndUp } = useDisplay()

// }}}

// {{{ restore saved display settings
const saved = loadPracticeSettings()
// }}}

// {{{ transformation (script style: vowels, trope, torah font) + tikkun
const { tikkunStyle, transformation } = useTransformation({ initialName: saved.transformationName })
const localFontSize = ref(saved.fontSize ?? storedFontSize.value)

watch(tikkunStyle, (enabled) => {
  savePracticeSettings({ tikkunStyle: enabled })
})

watch(transformation, (t) => {
  savePracticeSettings({ transformationName: t.name })
})

watch(localFontSize, (size) => {
  savePracticeSettings({ fontSize: size })
})
// }}}

// {{{ stable annotated sentences — built from props.sentences, NOT from AnnotatedText emit.
// This breaks the reactive cycle: segmentations → AnnotatedText → annotatedSentences → recordingsInStyle → segmentations.
const stableAnnotatedSentences = computed(() => props.sentences.map((s) => new AnnotatedSentence(s)))
// }}}

// {{{ voice style — auto-built from barMitzvah
const voiceStyleSet = ref<VoiceStyleSet>(defaultVoiceStyleSet())

const barMitzvah = computed(() => store.getters.student.barMitzvah)
const { annotations } = useAnnotations(barMitzvah)

// set pronunciation from barMitzvah (mirrors watcher 8 in usePracticeStyles)
watch(
  barMitzvah,
  (bm) => {
    if (bm) {
      voiceStyleSet.value.pronunciation = bm.pronunciation as PronunciationType
    }
  },
  { deep: true, immediate: true },
)

// set melody from selected content type (mirrors createMelodyWatcher in usePracticeStyles)
watch(
  () => props.aliyah,
  () => {
    if (props.aliyah && barMitzvah.value) {
      voiceStyleSet.value.torahMelody = barMitzvah.value.torahMelody ?? null
      voiceStyleSet.value.haftarahMelody = null
      voiceStyleSet.value.sentenceGroupMelody = null
    }
  },
  { immediate: true },
)

watch(
  () => props.haftarah,
  () => {
    if (props.haftarah && barMitzvah.value) {
      voiceStyleSet.value.haftarahMelody = barMitzvah.value.haftarahMelody ?? null
      voiceStyleSet.value.torahMelody = null
      voiceStyleSet.value.sentenceGroupMelody = null
    }
  },
  { immediate: true },
)

watch(
  () => props.sentenceGroup,
  () => {
    if (props.sentenceGroup && barMitzvah.value) {
      voiceStyleSet.value.sentenceGroupMelody = barMitzvah.value.sentenceGroupMelody ?? null
      voiceStyleSet.value.torahMelody = null
      voiceStyleSet.value.haftarahMelody = null
    }
  },
  { immediate: true },
)
// }}}

// {{{ sentence watcher — triggers fetchRecordings when sentences change
const sentencesRef = computed(() => props.sentences)
const { watchSentences } = useSentences(sentencesRef, logger)
watchSentences()
// }}}

// {{{ styled recordings — filters recordings by voice style
const { prepareStyledRecordings, recordingsInStyle } = useStyledRecordings(stableAnnotatedSentences, voiceStyleSet)
prepareStyledRecordings()
// }}}

// {{{ loose recording (after recordingsInStyle so focus points get segmentedSentence)
const looseRecording = useLooseRecording(logger, {
  aliyah: computed(() => props.aliyah),
  haftarah: computed(() => props.haftarah),
  playing,
  recordingsInStyle,
  sentenceGroup: computed(() => props.sentenceGroup),
  sentences: computed(() => props.sentences),
  voiceStyleSet,
})
// }}}

// {{{ voice mode (Read/Sing) — auto-detect from recordings
const voiceModeCounts = computed(() => {
  const counts = { read: 0, sung: 0 }
  for (const sentenceId of store.getters.audio.recordedSentenceIds) {
    for (const srmd of store.getters.audio.getRecordingListForSentence(sentenceId)) {
      if (!srmd.sentenceRecording) {
        continue
      }
      if (hasMelody(srmd.sentenceRecording)) {
        counts.sung += 1
      } else {
        counts.read += 1
      }
    }
  }
  return counts
})

const hasReadRecordings = computed(() => voiceModeCounts.value.read > 0)
const hasSungRecordings = computed(() => voiceModeCounts.value.sung > 0)
const showVoiceToggle = computed(() => hasReadRecordings.value && hasSungRecordings.value)

// auto-set voice mode: if only one mode has recordings, use it
watch(
  voiceModeCounts,
  (counts) => {
    if (counts.sung > 0 && counts.read === 0) {
      voiceStyleSet.value.voice = VoiceStyles[SING]
    } else if (counts.read > 0 && counts.sung === 0) {
      voiceStyleSet.value.voice = VoiceStyles[READ]
    }
  },
  { immediate: true },
)
// }}}

// {{{ choosable hidden tags — discover granularity levels with recordings
const { choosableHiddenTags } = useChoosableTags(sentencesRef, voiceStyleSet, logger)

// slider model: index into availableGranularities
const selectedGranularityIndex = ref(saved.granularityIndex ?? 0)

// available granularities in left-to-right order: sentence → phrases → words
const GRANULARITY_ORDER = [SENTENCE, PHRASES, WORDS]
const availableGranularities = computed(() => {
  const tags = choosableHiddenTags.value
  return GRANULARITY_ORDER.filter((name) => findTagByName(tags, name))
})

const selectedGranularity = computed(() => availableGranularities.value[selectedGranularityIndex.value] ?? null)

// when available granularities change, clamp slider to valid range
watch(availableGranularities, (newVal) => {
  if (newVal.length && selectedGranularityIndex.value >= newVal.length) {
    selectedGranularityIndex.value = 0
  }
})

watch(selectedGranularityIndex, (index) => {
  savePracticeSettings({ granularityIndex: index })
})

// when slider changes, update voiceStyleSet.tags with the matching hidden tag
watch(
  selectedGranularity,
  (granularity) => {
    if (!granularity) {
      voiceStyleSet.value.tags = []
      return
    }
    const tag = findTagByName(choosableHiddenTags.value, granularity)
    voiceStyleSet.value.tags = tag ? [tag] : []
  },
  { immediate: true },
)

const GRANULARITY_LABELS: Record<string, string> = { [PHRASES]: 'Phrases', [SENTENCE]: 'Verse', [WORDS]: 'Words' }
const sliderLabel = (index: number) => GRANULARITY_LABELS[availableGranularities.value[index]] ?? ''
// }}}

// {{{ segmentations — derived from recordings, keyed by props.sentences (not AnnotatedText emit)
// When segmentationFromRecording returns [] (e.g. sentence-level recordings with no segmentedSentence),
// AnnotatedSentence falls back to word-per-segment. Use makeSentenceSegmentation as fallback
// (same pattern as RecordingsView.vue).
const segmentations = computed<SegmentationType[]>(() => {
  return props.sentences.map((sentence, i) => {
    const recs = recordingsInStyle.value[sentence.id]
    if (!recs?.length) {
      return []
    }
    const seg = segmentationFromRecording(recs[0])
    if (seg.length) {
      return seg
    }
    const as = stableAnnotatedSentences.value[i]
    return as ? makeSentenceSegmentation(as) : []
  })
})
// }}}

// {{{ popover + playback
type ListenSegmentKey = { segmentIndex: number; sentenceIndex: number }

const playback = usePopoverPlayback<ListenSegmentKey>({
  equals: (a, b) => a.sentenceIndex === b.sentenceIndex && a.segmentIndex === b.segmentIndex,
  paused,
  playFromStart: ({ segmentIndex, sentenceIndex }) => {
    const sentence = props.sentences[sentenceIndex]
    if (!sentence) {
      return
    }
    const recs = recordingsInStyle.value[sentence.id]
    if (recs?.length) {
      playSegment(recs[0], segmentIndex)
    }
  },
  resume,
})

const {
  close: closePopover,
  data: popoverData,
  open: openPopover,
  visible: popoverVisible,
} = usePopover<{ sentenceIndex: number; segmentIndex: number }>({
  onBeforeClose: () => {
    pause()
    playback.reset()
  },
})

const popoverTarget = computed<string | null>(() => {
  if (!popoverData.value) {
    return null
  }
  return listenTextSegmentId(popoverData.value.sentenceIndex, popoverData.value.segmentIndex)
})

const openPopoverAndPlay = (sentenceIndex: number, segmentIndex: number) => {
  openPopover({ segmentIndex, sentenceIndex })
  playback.start({ segmentIndex, sentenceIndex })
}

const hasSegmentRecording = (
  _annotatedSentence: AnnotatedSentence | null,
  _annotatedSegment: AnnotatedSegment | null,
  sentenceIndex: number,
  _segmentIndex: number,
): boolean => {
  const sentence = props.sentences[sentenceIndex]
  if (!sentence) {
    return false
  }
  return looseRecording.isRecording.value || !!recordingsInStyle.value[sentence.id]?.length
}

const hasSegmentRecordingWordWrapper = segmentPredicateAsWordPredicate(hasSegmentRecording)

const handleClickSegment = (
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
) => {
  looseRecording.pushSentenceFocusPoint(sentenceIndex, segmentIndex)
  if (looseRecording.isRecording.value) {
    return
  }
  if (!hasSegmentRecording(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)) {
    return
  }
  openPopoverAndPlay(sentenceIndex, segmentIndex)
}

const handleClickWord = (
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  _annotatedWord: AnnotatedWord,
  sentenceIndex: number,
  segmentIndex: number,
  _wordIndex: number,
) => {
  // Don't push focus point here — handleClickSegment also fires and pushes
  if (looseRecording.isRecording.value) {
    return
  }
  if (!hasSegmentRecording(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)) {
    return
  }
  openPopoverAndPlay(sentenceIndex, segmentIndex)
}

const handlePopoverPlay = () => {
  const data = popoverData.value
  if (!data) {
    return
  }
  playback.playOrResume({ segmentIndex: data.segmentIndex, sentenceIndex: data.sentenceIndex })
}

const handlePopoverRestart = () => {
  const data = popoverData.value
  if (!data) {
    return
  }
  playback.start({ segmentIndex: data.segmentIndex, sentenceIndex: data.sentenceIndex })
}

const segmentStyle = (
  style: string,
  _annotatedSentence: AnnotatedSentence,
  _annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
): string => {
  const sentence = props.sentences[sentenceIndex]
  if (!sentence || !recordingsInStyle.value[sentence.id]?.length) {
    return absentStyle
  }
  return segmentations.value.length ? SEGMENT_STYLES[segmentIndex % 2] : style
}
// }}}

// {{{ voice toggle handler
const toggleVoice = () => {
  if (voiceStyleSet.value.voice === VoiceStyles[READ]) {
    voiceStyleSet.value.voice = VoiceStyles[SING]
  } else {
    voiceStyleSet.value.voice = VoiceStyles[READ]
  }
}
// }}}

// {{{ pitch shift
const uiPitchShift = ref(saved.uiPitchShift ?? 0)
const showPitchShift = computed(() => inSingMode(voiceStyleSet.value))

watch(uiPitchShift, (value) => {
  pitchShift.value = uiPitchToActual(value)
  savePracticeSettings({ uiPitchShift: value })
})
// }}}
</script>

<template>
  <div>
    <!-- Desktop toolbar: all controls inline -->
    <v-row v-if="mdAndUp" align="center" class="practice-toolbar mb-4 ga-10" no-gutters>
      <!-- Voice toggle (Read/Sing) -->
      <v-col v-if="showVoiceToggle" cols="auto">
        <v-btn-toggle color="primary" density="compact" mandatory :model-value="voiceStyleSet.voice" @update:model-value="toggleVoice">
          <v-btn size="small" :value="VoiceStyles[READ]">{{ VoiceStyles[READ] }}</v-btn>
          <v-btn size="small" :value="VoiceStyles[SING]">{{ VoiceStyles[SING] }}</v-btn>
        </v-btn-toggle>
      </v-col>

      <!-- Granularity slider with text labels -->
      <v-col v-if="availableGranularities.length > 1" cols="auto">
        <div class="d-flex align-center" style="gap: 8px">
          <span class="text-caption text-medium-emphasis">{{ sliderLabel(0) }}</span>
          <v-slider
            v-model="selectedGranularityIndex"
            color="primary"
            density="compact"
            hide-details
            :max="availableGranularities.length - 1"
            :min="0"
            :step="1"
            :style="GRANULARITY_SLIDER_STYLE"
          />
          <span class="text-caption text-medium-emphasis">{{ sliderLabel(availableGranularities.length - 1) }}</span>
        </div>
      </v-col>

      <!-- Script style selector + tikkun toggle -->
      <v-col cols="auto">
        <div class="d-flex align-center ga-4">
          <transformation-select
            :include-torah-font="!!aliyah"
            :model-value="transformation"
            @update:model-value="transformation = $event"
          />
          <tikkun-toggle v-if="!!aliyah" v-model="tikkunStyle" />
        </div>
      </v-col>

      <!-- Font size slider with A↓ / A↑ buttons -->
      <v-col cols="auto">
        <font-size-controls v-model="localFontSize" :reset-value="storedFontSize" />
      </v-col>

      <!-- Pitch shift slider (sing mode only) -->
      <v-col v-if="showPitchShift" cols="auto">
        <div class="d-flex align-center ga-2">
          <v-btn
            density="compact"
            :disabled="uiPitchShift <= -UI_MIN_PITCH_SHIFT"
            icon
            size="small"
            variant="text"
            @click="uiPitchShift = Math.max(-UI_MIN_PITCH_SHIFT, uiPitchShift - PITCH_SHIFT_STEP)"
          >
            <span class="pitch-btn"><v-icon>mdi-music-note</v-icon><v-icon size="x-small">mdi-arrow-down</v-icon></span>
          </v-btn>
          <v-slider
            v-model="uiPitchShift"
            color="primary"
            density="compact"
            hide-details
            :max="UI_MAX_PITCH_SHIFT"
            :min="-1 * UI_MIN_PITCH_SHIFT"
            :step="PITCH_SHIFT_STEP"
            :style="PITCH_SLIDER_STYLE"
            thumb-label
            @dblclick="uiPitchShift = 0"
          />
          <v-btn
            density="compact"
            :disabled="uiPitchShift >= UI_MAX_PITCH_SHIFT"
            icon
            size="small"
            variant="text"
            @click="uiPitchShift = Math.min(UI_MAX_PITCH_SHIFT, uiPitchShift + PITCH_SHIFT_STEP)"
          >
            <span class="pitch-btn"><v-icon>mdi-music-note</v-icon><v-icon size="x-small">mdi-arrow-up</v-icon></span>
          </v-btn>
        </div>
      </v-col>

      <!-- Record / Stop + spinning indicator + phrase-split blip -->
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <v-btn
            :class="{ 'mic-idle': !looseRecording.isRecording.value, 'phrase-split-blip': looseRecording.phraseSplitDetected.value }"
            color="error"
            icon
            size="small"
            variant="flat"
            @click="looseRecording.toggleRecord()"
          >
            <v-icon>{{ looseRecording.isRecording.value ? 'mdi-stop' : 'mdi-microphone' }}</v-icon>
          </v-btn>
          <recording-spinner v-if="looseRecording.isRecording.value" :size="36" />
        </div>
      </v-col>
    </v-row>

    <!-- Mobile toolbar: compact controls + settings gear -->
    <div v-else class="practice-toolbar d-flex align-center ga-3">
      <!-- Voice toggle (Read/Sing) -->
      <v-btn-toggle
        v-if="showVoiceToggle"
        color="primary"
        density="compact"
        mandatory
        :model-value="voiceStyleSet.voice"
        @update:model-value="toggleVoice"
      >
        <v-btn size="small" :value="VoiceStyles[READ]">{{ VoiceStyles[READ] }}</v-btn>
        <v-btn size="small" :value="VoiceStyles[SING]">{{ VoiceStyles[SING] }}</v-btn>
      </v-btn-toggle>

      <!-- Granularity slider: SVG icons -->
      <div v-if="availableGranularities.length > 1" class="d-flex align-center" style="gap: 4px">
        <svg class="granularity-icon" viewBox="0 0 20 8" xmlns="http://www.w3.org/2000/svg">
          <rect fill="currentColor" height="4" rx="2" width="18" x="1" y="2" />
        </svg>
        <v-slider
          v-model="selectedGranularityIndex"
          color="primary"
          density="compact"
          hide-details
          :max="availableGranularities.length - 1"
          :min="0"
          :step="1"
          :style="COMPACT_GRANULARITY_SLIDER_STYLE"
        />
        <svg class="granularity-icon" viewBox="0 0 20 8" xmlns="http://www.w3.org/2000/svg">
          <rect fill="currentColor" height="4" rx="1" width="5" x="1" y="2" />
          <rect fill="currentColor" height="4" rx="1" width="5" x="8" y="2" />
          <rect fill="currentColor" height="4" rx="1" width="5" x="15" y="2" />
        </svg>
      </div>

      <v-divider class="mx-1" length="20" vertical />

      <!-- Font size A↓ / A↑ buttons (no slider on mobile) -->
      <font-size-controls v-model="localFontSize" :show-slider="false" />

      <v-divider class="mx-2" length="20" vertical />

      <!-- Settings gear → popover with script style + pitch shift -->
      <v-menu :close-on-content-click="false" location="bottom start">
        <template #activator="{ props: menuProps }">
          <v-btn density="compact" icon="mdi-cog" size="small" variant="text" v-bind="menuProps" />
        </template>
        <v-card class="pa-4 d-flex flex-column ga-4" min-width="280">
          <div class="d-flex align-center ga-4">
            <transformation-select
              :include-torah-font="!!aliyah"
              :model-value="transformation"
              @update:model-value="transformation = $event"
            />
            <tikkun-toggle v-if="!!aliyah" v-model="tikkunStyle" />
          </div>

          <div v-if="showPitchShift" class="d-flex align-center ga-2">
            <v-btn
              density="compact"
              :disabled="uiPitchShift <= -UI_MIN_PITCH_SHIFT"
              icon
              size="small"
              variant="text"
              @click="uiPitchShift = Math.max(-UI_MIN_PITCH_SHIFT, uiPitchShift - PITCH_SHIFT_STEP)"
            >
              <span class="pitch-btn"><v-icon>mdi-music-note</v-icon><v-icon size="x-small">mdi-arrow-down</v-icon></span>
            </v-btn>
            <v-slider
              v-model="uiPitchShift"
              color="primary"
              density="compact"
              hide-details
              :max="UI_MAX_PITCH_SHIFT"
              :min="-1 * UI_MIN_PITCH_SHIFT"
              :step="PITCH_SHIFT_STEP"
              thumb-label
              @dblclick="uiPitchShift = 0"
            />
            <v-btn
              density="compact"
              :disabled="uiPitchShift >= UI_MAX_PITCH_SHIFT"
              icon
              size="small"
              variant="text"
              @click="uiPitchShift = Math.min(UI_MAX_PITCH_SHIFT, uiPitchShift + PITCH_SHIFT_STEP)"
            >
              <span class="pitch-btn"><v-icon>mdi-music-note</v-icon><v-icon size="x-small">mdi-arrow-up</v-icon></span>
            </v-btn>
          </div>
        </v-card>
      </v-menu>

      <v-spacer />

      <!-- Record / Stop + spinning indicator + phrase-split blip -->
      <div class="d-flex align-center ga-2">
        <v-btn
          :class="{ 'mic-idle': !looseRecording.isRecording.value, 'phrase-split-blip': looseRecording.phraseSplitDetected.value }"
          color="error"
          icon
          size="small"
          variant="flat"
          @click="looseRecording.toggleRecord()"
        >
          <v-icon>{{ looseRecording.isRecording.value ? 'mdi-stop' : 'mdi-microphone' }}</v-icon>
        </v-btn>
        <recording-spinner v-if="looseRecording.isRecording.value" :size="36" />
      </div>
    </div>

    <!-- Text display -->
    <annotated-text
      :annotations="annotations"
      :break-before-line="() => !tikkunStyle"
      :clickable-segment="hasSegmentRecording"
      :clickable-word="hasSegmentRecordingWordWrapper"
      :default-font="font"
      :font-size="localFontSize"
      :line-height="lineHeight"
      :name="LISTEN_TEXT_NAME"
      :segment-style="segmentStyle"
      :segmentations="segmentations"
      :sentences="sentences"
      :show-end-of-sentence-character="!tikkunStyle"
      :show-sentence-numbers="!tikkunStyle"
      :suppress-colors="() => tikkunStyle"
      :transformation="transformation"
      @click-segment="handleClickSegment"
      @click-word="handleClickWord"
    />

    <!-- Playback popover -->
    <pop-over :attach-id="popoverTarget" :visible="popoverVisible">
      <v-card>
        <v-card-text class="pa-1">
          <v-icon v-if="!playing" class="playable" size="xxx-large" @click="handlePopoverPlay"> mdi-play mdi-rotate-180 </v-icon>
          <v-icon v-else class="pausable" size="xxx-large" @click="pause()">mdi-pause</v-icon>
          <v-icon class="playable" size="xxx-large" @click="handlePopoverRestart">mdi-restart</v-icon>
          <v-icon size="xxx-large" @click="closePopover()">mdi-close</v-icon>
        </v-card-text>
      </v-card>
    </pop-over>

    <!-- Sampling overlay (background noise calibration) -->
    <sampling-overlay
      :on-click="() => looseRecording.toggleRecord()"
      :percent="looseRecording.backgroundSamplingPercent.value"
      :visible="looseRecording.backgroundSampling.value"
    />

    <!-- Recording upload dialog -->
    <recording-upload-dialog
      :blob-url="looseRecording.pendingRecordingBlobUrl.value"
      :description="looseRecording.practiceDescription.value"
      :uploading="looseRecording.uploading.value"
      :visible="looseRecording.uploadRecordingOverlay.value"
      @discard="looseRecording.discardRecording()"
      @save="looseRecording.saveRecording()"
      @share="looseRecording.shareRecording()"
    />
  </div>
</template>

<style>
.pitch-btn {
  display: flex;
  align-items: center;
}

.mic-idle.v-btn {
  background-color: #e57373 !important;
  color: white !important;
}

.phrase-split-blip.v-btn {
  animation: phrase-blink 0.6s ease-in-out;
}

@keyframes phrase-blink {
  0% {
    opacity: 1;
  }
  15% {
    opacity: 0.15;
  }
  30% {
    opacity: 1;
  }
  45% {
    opacity: 0.15;
  }
  60% {
    opacity: 1;
  }
}

.granularity-icon {
  width: 20px;
  height: 8px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  flex-shrink: 0;
}

.practice-toolbar {
  position: sticky;
  top: var(--app-bar-height);
  z-index: 10;
  background: rgb(var(--v-theme-surface));
  padding-block: 8px;
  margin-inline: -16px;
  padding-inline: 16px;
}
</style>
